/**
 * Bundle Configurator Modal
 *
 * When a Product Bundle item is clicked in the ChatOrderSheet product picker,
 * this modal opens to let staff select variant options (Color, Size, etc.)
 * for each component in the bundle — mirroring the storefront's
 * "Customize Your Bundle" experience.
 *
 * Uses the existing `get_bundle_for_storefront` API (storefront/api_bundle.py).
 * Returns bundle_variant_selections dict to parent for Quotation storage.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogHeader,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Package, Check } from 'lucide-react';
import { useFrappeFileUrl } from '@/lib/utils/frappeUrl';
import { frappeMethod } from '@/lib/erp/utils';
import { cn } from '@/lib/utils';
import { formatCurrency } from './formatCurrency';

/** Shape of each component in interactive mode from get_bundle_for_storefront */
interface BundleComponent {
  item_code: string;
  item_name: string;
  image: string | null;
  qty: number;
  price: number | null;
  attributes: Array<{
    attribute: string;
    values: Array<{ value: string; image: string | null; available: boolean }>;
  }>;
  default_variant: string | null;
  variant_prices?: Record<string, number>;
}

interface BundleForStorefront {
  is_bundle: boolean;
  allow_variant_selection: boolean;
  variant_pricing: boolean;
  linked_attributes: string[];
  items: BundleComponent[];
  total_component_price: number;
  bundle_price: number | null;
  currency: string;
}

interface BundleConfiguratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemCode: string;
  tenantKey: string;
  currency: string;
  /** Called with selections + computed surcharge from variant pricing */
  onConfirm: (itemCode: string, selections: Record<string, Record<string, string>>, surcharge: number) => void;
}

export function BundleConfiguratorModal({
  open,
  onOpenChange,
  itemCode,
  tenantKey,
  currency,
  onConfirm,
}: BundleConfiguratorModalProps) {
  const toAbsolute = useFrappeFileUrl();

  // Per-component attribute selections: { componentCode: { attr: value } }
  const [selections, setSelections] = useState<Record<string, Record<string, string>>>({});

  // Fetch bundle data from existing storefront API
  const { data, isLoading } = useQuery({
    queryKey: ['bundle-for-storefront', tenantKey, itemCode],
    queryFn: () =>
      frappeMethod<BundleForStorefront>(
        'zaviago_backend.storefront.api_bundle.get_bundle_for_storefront',
        { tenant: tenantKey, item_code: itemCode }
      ),
    enabled: open && !!itemCode && !!tenantKey,
  });

  // Reset selections when modal opens with new item
  useEffect(() => {
    if (open) {
      setSelections({});
    }
  }, [open, itemCode]);

  // Linked attributes — selecting one syncs across all components
  const linkedAttributes = useMemo(() => data?.linked_attributes ?? [], [data]);

  const handleAttributeSelect = useCallback(
    (componentCode: string, attribute: string, value: string) => {
      setSelections((prev) => {
        const updated = { ...prev };

        // Update the target component
        updated[componentCode] = { ...(updated[componentCode] ?? {}), [attribute]: value };

        // If this is a linked attribute, sync to all other components that have it
        if (linkedAttributes.includes(attribute) && data?.items) {
          for (const comp of data.items) {
            if (comp.item_code === componentCode) continue;
            // Check if this component has the same attribute with available value
            const attrGroup = comp.attributes.find((a) => a.attribute === attribute);
            if (attrGroup) {
              const valueExists = attrGroup.values.some(
                (v) => v.value === value && v.available
              );
              if (valueExists) {
                updated[comp.item_code] = {
                  ...(updated[comp.item_code] ?? {}),
                  [attribute]: value,
                };
              }
            }
          }
        }

        return updated;
      });
    },
    [linkedAttributes, data]
  );

  // Check if all required attributes are selected for all components
  const isComplete = useMemo(() => {
    if (!data?.items) return false;
    return data.items.every((comp) => {
      if (comp.attributes.length === 0) return true; // No attributes needed
      const compSelections = selections[comp.item_code];
      if (!compSelections) return false;
      return comp.attributes.every((attr) => !!compSelections[attr.attribute]);
    });
  }, [data, selections]);

  // Compute variant surcharge from selections using variant_prices map
  // surcharge = sum(selected_variant_price - min(component_variant_prices)) per component
  const surcharge = useMemo(() => {
    if (!data?.variant_pricing || !data?.items) return 0;
    let total = 0;
    for (const comp of data.items) {
      if (!comp.variant_prices || !selections[comp.item_code]) continue;
      const compSel = selections[comp.item_code];
      // Build the same key format as the server: "Color:Red|Size:M" (sorted by attr name)
      const key = Object.entries(compSel)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([attr, val]) => `${attr}:${val}`)
        .join('|');
      const selectedPrice = comp.variant_prices[key];
      const basePrice = comp.price; // min of all variant prices (from server)
      if (selectedPrice != null && basePrice != null && selectedPrice > basePrice) {
        total += selectedPrice - basePrice;
      }
    }
    return Math.round(total * 100) / 100;
  }, [data, selections]);

  const handleConfirm = useCallback(() => {
    if (!isComplete) return;
    onConfirm(itemCode, selections, surcharge);
    onOpenChange(false);
  }, [isComplete, itemCode, selections, surcharge, onConfirm, onOpenChange]);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setSelections({});
      }
      onOpenChange(newOpen);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-lg leading-snug">Customize Your Bundle</DialogTitle>
          <DialogDescription>
            Select options for each item in the bundle
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="px-6 py-12 text-center text-muted-foreground">
            <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            Loading bundle...
          </div>
        ) : !data || !data.is_bundle ? (
          <div className="px-6 py-12 text-center text-muted-foreground">
            This item is not a bundle or has no variant selection.
          </div>
        ) : (
          <>
            {/* Scrollable content — one section per component */}
            <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-6">
              {data.items.map((comp) => {
                const compSelections = selections[comp.item_code] ?? {};
                const isCompComplete =
                  comp.attributes.length === 0 ||
                  comp.attributes.every((a) => !!compSelections[a.attribute]);

                return (
                  <div key={comp.item_code} className="space-y-3">
                    {/* Component header */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-md overflow-hidden bg-muted flex items-center justify-center shrink-0">
                        {comp.image ? (
                          <img
                            src={toAbsolute(comp.image) ?? undefined}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Package className="w-5 h-5 text-muted-foreground/40" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{comp.item_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {comp.qty > 1 && `x${comp.qty} · `}
                          {comp.price != null ? formatCurrency(comp.price, currency) : ''}
                        </p>
                      </div>
                      {isCompComplete && (
                        <Check className="w-4 h-4 text-green-600 shrink-0" />
                      )}
                    </div>

                    {/* Attribute selectors */}
                    {comp.attributes.map((attrGroup) => (
                      <div key={attrGroup.attribute} className="space-y-1.5 pl-[52px]">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {attrGroup.attribute}
                        </label>
                        <div className="flex flex-wrap gap-1.5">
                          {attrGroup.values.map((opt) => {
                            const selected = compSelections[attrGroup.attribute] === opt.value;
                            return (
                              <button
                                key={opt.value}
                                disabled={!opt.available}
                                onClick={() =>
                                  handleAttributeSelect(comp.item_code, attrGroup.attribute, opt.value)
                                }
                                className={cn(
                                  'px-3 py-1.5 rounded text-xs font-medium transition-all',
                                  selected
                                    ? 'bg-[#007AFF] text-white shadow-sm'
                                    : opt.available
                                      ? 'bg-muted/60 hover:bg-muted text-foreground'
                                      : 'bg-muted/30 text-muted-foreground/50 cursor-not-allowed line-through'
                                )}
                              >
                                {opt.value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6 pt-3 border-t border-border/50 shrink-0 space-y-3">
              {/* Bundle price summary — shows surcharge-adjusted price when variant pricing is active */}
              {data.bundle_price != null && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Bundle price</span>
                  <span className="font-semibold">
                    {formatCurrency(data.bundle_price + surcharge, currency)}
                  </span>
                </div>
              )}

              <Button
                className="w-full h-11 rounded bg-[#007AFF] hover:bg-[#007AFF]/90 text-white text-sm font-semibold"
                disabled={!isComplete}
                onClick={handleConfirm}
              >
                {isComplete ? 'Add Bundle to Cart' : 'Select all options'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
